import {
  ContentfulOffering,
  OfferingSKUMap,
  PricingPlans,
  ProductKeyMap
} from '@atlassiansox/asc-product-catalog';
import { AsyncResult, useAsync } from '@atlassiansox/bxp-react-core';
import { useState } from 'react';
import { FetchPricing } from './models';
import {
  CurrencyISOCode,
  FormattedPriceCalculationResponse,
  PriceCalculationRequest
} from './models/price-req-response';
import { calculatePrice, extractPricingTiersFromEntry } from './utils';
import getFreePriceCalc from './utils/get-free-price-calc';

export function usePricing(
  getPriceRequests: readonly PriceCalculationRequest[],
  fetchPricing: FetchPricing,
  currencyISOCode: CurrencyISOCode,
  localeISOCode: string
): AsyncResult<FormattedPriceCalculationResponse[]> {
  const [cachedHAMSPricingPlansByKey, setCachedHAMSPricingPlansByKey] =
    useState<undefined | ProductKeyMap<PricingPlans>>();

  const [cachedCCPOfferingsBySKU, setCachedCCPOfferingsBySKU] = useState<
    undefined | OfferingSKUMap<ContentfulOffering>
  >();

  return useAsync(async () => {
    const offeringSKUs = new Set<string>();
    const hamsProductKeys = new Set<string>();

    for (const getPriceRequest of getPriceRequests) {
      const { ccpOfferingSKU, hamsProductKey } = getPriceRequest;

      if (
        [ccpOfferingSKU, hamsProductKey].every(
          (v) => v === null || v === undefined || v === ''
        )
      ) {
        console.error(
          `Request for pricing did not contain CCP SKU or HAMS product key, request will be skipped: ${JSON.stringify(
            getPriceRequest
          )}`
        );
      }

      if (ccpOfferingSKU) {
        offeringSKUs.add(ccpOfferingSKU);
      }

      if (hamsProductKey) {
        hamsProductKeys.add(hamsProductKey);
      }
    }

    const uncachedCCPSKUs = [];
    for (const sku of offeringSKUs) {
      if (!cachedCCPOfferingsBySKU || !cachedCCPOfferingsBySKU[sku]) {
        uncachedCCPSKUs.push(sku);
      }
    }

    const uncachedHAMSProductKeys = [];
    for (const key of hamsProductKeys) {
      if (!cachedHAMSPricingPlansByKey || !cachedHAMSPricingPlansByKey[key]) {
        uncachedHAMSProductKeys.push(key);
      }
    }

    let ccpOfferingsBySKU = cachedCCPOfferingsBySKU;
    if (uncachedCCPSKUs.length) {
      const newCCPOfferingsBySKU = await fetchPricing.getOfferingsBySKU({
        offeringSKUs: Array.from(uncachedCCPSKUs)
      });

      ccpOfferingsBySKU = {
        ...cachedCCPOfferingsBySKU,
        ...newCCPOfferingsBySKU
      };

      setCachedCCPOfferingsBySKU(ccpOfferingsBySKU);
    }

    let hamsPricingPlansByKey = cachedHAMSPricingPlansByKey;
    if (uncachedHAMSProductKeys.length) {
      const newHamsPricingPlansByKey = await fetchPricing.getPricing({
        productKeys: Array.from(uncachedHAMSProductKeys)
      });

      hamsPricingPlansByKey = {
        ...cachedHAMSPricingPlansByKey,
        ...newHamsPricingPlansByKey
      };

      setCachedHAMSPricingPlansByKey(hamsPricingPlansByKey);
    }

    const priceCalculations = [];
    for (const {
      ccpOfferingSKU,
      hamsProductKey,
      numUnits,
      period
    } of getPriceRequests) {
      if (numUnits === 0) {
        priceCalculations.push(getFreePriceCalc(currencyISOCode));
      } else {
        const pricingTiers = extractPricingTiersFromEntry(
          ccpOfferingsBySKU as OfferingSKUMap<ContentfulOffering>,
          hamsPricingPlansByKey as ProductKeyMap<PricingPlans>,
          period,
          currencyISOCode,
          ccpOfferingSKU,
          hamsProductKey
        );

        if (!pricingTiers) {
          throw new Error(`Pricing tiers not found for ${period}.`);
        }

        priceCalculations.push(
          calculatePrice(numUnits, pricingTiers, currencyISOCode, localeISOCode)
        );
      }
    }

    return priceCalculations;
  }, [getPriceRequests, currencyISOCode, localeISOCode]);
}
